/**
 * V114-5-1 — client for the gap-fulfillment surface (Track 1 routes) and the outbound webhook
 * admin (Track 2 routes). Kept out of lib/api.ts so parallel tracks don't collide on one file.
 * Every call is workspace-scoped (`/api/w/:slug/...`); the server resolves the workspace from the
 * session and 404s on any id outside it.
 *
 * Routes (packages/cloudflare/src/routes):
 *   gaps.ts      GET /gaps(?system=) · GET /gaps/:gapKey/brief · POST /gaps/:gapKey/decline
 *                (reads: any member; decline: gated per family by the decline service, D-114-10)
 *   webhooks.ts  GET/POST / · DELETE /:id · POST /:id/rotate-secret · GET /:id/deliveries
 *                (ALL owner / admin — requireRole on the whole router)
 */
import type {
  FulfillmentBrief,
  FulfillmentBriefOption,
  FulfillmentGap,
  FulfillmentOptionKind,
  GapFamily,
  OutboundWebhookDelivery,
  OutboundWebhookEndpoint,
} from '@aigovops/core';
import { req } from './api';
import { scopeOutFailing, scopeOutPassing, whoCanText, type Capability, type CapabilityContext } from './monitoring-capabilities';

export type { FulfillmentBrief, FulfillmentBriefOption, FulfillmentGap, FulfillmentOptionKind, GapFamily };

const enc = encodeURIComponent;
const ws = (slug: string) => `/api/w/${enc(slug)}`;
const send = <T>(path: string, method: string, body?: unknown) =>
  req<T>(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ---------------------------------------------------------------------------
// Gaps (V114-1-6)
// ---------------------------------------------------------------------------

/** GET /gaps — any member. One shape over the four families, computed on read. */
export const listGaps = (slug: string, systemId?: string | null) =>
  req<{ gaps: FulfillmentGap[]; generated_at: string }>(`${ws(slug)}/gaps${systemId ? `?system=${enc(systemId)}` : ''}`);

/** GET /gaps/:gapKey/brief — any member. 404 for a key the recompute no longer produces. */
export const getGapBrief = (slug: string, gapKey: string) =>
  req<{ gap: FulfillmentGap; brief: FulfillmentBrief }>(`${ws(slug)}/gaps/${enc(gapKey)}/brief`);

/** POST /gaps/:gapKey/decline — gated per family (D-114-10); the reason is required. */
export const declineGap = (slug: string, gapKey: string, reason: string) =>
  send<{ declined: { gap_key: string; family: GapFamily; via: 'ci_scope' | 'control_na' | 'enabler_declined'; record: Record<string, unknown> } }>(
    `${ws(slug)}/gaps/${enc(gapKey)}/decline`, 'POST', { reason });

/**
 * DELETE /systems/:id/controls/:controlId/not-applicable (panel B1) — reopen a control that was
 * marked Not applicable. Owner / admin (the N/A route's own gate); audited server-side.
 */
export const reopenControlNotApplicable = (slug: string, systemId: string, controlId: string) =>
  send<{ state: { status: string } }>(`${ws(slug)}/systems/${enc(systemId)}/controls/${enc(controlId)}/not-applicable`, 'DELETE');

// ---------------------------------------------------------------------------
// Display helpers (pure; words from what the API returned — D-113-26: never "CI")
// ---------------------------------------------------------------------------

/** The typed gap reasons in people's words. */
const GAP_REASON_LABELS: Record<string, string> = {
  no_method: 'No method',
  method_unverified: 'Method not verified',
  binding_missing: 'No method',
  binding_not_fresh: 'Past cadence',
  last_check_not_passing: 'Stopped passing',
  review_due: 'Review due',
  ci_gone: 'Configuration item gone',
  no_relevant_ci: 'No relevant configuration item',
  unmonitored_ci: 'Unmonitored configuration item',
  control_check_failure: 'Failing check',
  release_gap: 'Release gap',
  enabler_gap: 'Setup step open',
};
const humanize = (s: string) => s.replace(/[_-]+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
export const gapReasonLabel = (reason: string): string => GAP_REASON_LABELS[reason] ?? humanize(reason);

export const GAP_OPTION_KIND_LABELS: Record<FulfillmentOptionKind, string> = {
  in_app: 'In GAC Pro',
  templated: 'With your agent',
  bespoke: 'Your own change',
  admin_console: 'Admin console',
};

/**
 * D-114-10: what a decline DOES, spelled out before confirm — the governing record per family.
 * Null = this family cannot be declined (a release gap closes by evidence only).
 */
export function declineConsequence(gap: Pick<FulfillmentGap, 'family' | 'component_id'>): string | null {
  if (gap.family === 'release') return null;
  if (gap.family === 'enabler') return 'Declines the setup step. It stays declined until someone reopens it.';
  if (gap.family === 'monitoring' && gap.component_id) return 'Takes this configuration item out of scope for this system. It stops counting toward every control of the system.';
  return 'Marks the control Not applicable for this system. It leaves the coverage score until someone reopens it.';
}

/**
 * D-114-10 role gates, mirrored from the decline service (the server stays the authority):
 *   configuration-item gap → editor bar; owner / admin while its check fails (D-113-28);
 *   control gap / failing check → owner / admin (ADR-0032 N/A);
 *   setup step → owner / admin or a current program lead.
 */
export function declineCapability(
  gap: Pick<FulfillmentGap, 'family' | 'component_id' | 'reason'>,
  ctx: CapabilityContext & { programLead?: boolean },
): Capability {
  const role = ctx.role ?? '';
  const admin = role === 'owner' || role === 'admin';
  if (gap.family === 'release') return { allowed: false, whoCan: 'A release gap closes by evidence only; it cannot be declined.' };
  if (gap.family === 'enabler') {
    return admin || ctx.programLead
      ? { allowed: true, whoCan: null }
      : { allowed: false, whoCan: 'Only an owner, admin or program lead can decline a setup step.' };
  }
  if (gap.family === 'monitoring' && gap.component_id) {
    if (gap.reason === 'last_check_not_passing') {
      const cap = scopeOutFailing(ctx);
      return cap.allowed ? cap : { allowed: false, whoCan: 'A failing configuration item can only be scoped out by an owner or admin.' };
    }
    return scopeOutPassing(ctx);
  }
  return admin ? { allowed: true, whoCan: null } : { allowed: false, whoCan: whoCanText(['owner', 'admin']) };
}

/** The decline action named by its consequence (panel H5), per family. Null = not declinable. */
export function declineActionLabel(gap: Pick<FulfillmentGap, 'family' | 'component_id'>): string | null {
  if (gap.family === 'release') return null;
  if (gap.family === 'enabler') return 'Decline step…';
  if (gap.family === 'monitoring' && gap.component_id) return 'Scope out item…';
  return 'Mark control N/A…';
}

/** A family-matched example for the required decline reason (the item gap speaks about the ITEM). */
export function declineReasonPlaceholder(gap: Pick<FulfillmentGap, 'family' | 'component_id'>): string {
  if (gap.family === 'enabler') return 'e.g. We submit this evidence through a different pipeline';
  if (gap.family === 'monitoring' && gap.component_id) return 'e.g. This item carries no model traffic for this system';
  return 'e.g. This control is handled upstream by the gateway';
}

/**
 * Panel H3: the brief's prose for the screen — the control's display title in place of its id and
 * the typed reason in words. Raw keys stay only in the copyable command / markdown.
 */
export function briefDisplayText(text: string, gap: Pick<FulfillmentGap, 'control_id' | 'reason'>, controlTitle?: string | null): string {
  let out = text;
  if (gap.control_id && controlTitle && controlTitle !== gap.control_id) {
    out = out.split(gap.control_id).join(controlTitle);
  }
  // "(no_method)" → "(no method)"; "reason no_method clears" → "reason “No method” clears".
  out = out.split(`(${gap.reason})`).join(`(${gapReasonLabel(gap.reason).toLowerCase()})`);
  out = out.split(`reason ${gap.reason}`).join(`reason “${gapReasonLabel(gap.reason)}”`);
  return out;
}

// ---------------------------------------------------------------------------
// Brief → markdown ("copy brief" renders what the server produced; nothing invented)
// ---------------------------------------------------------------------------

function optionMarkdown(o: FulfillmentBriefOption, i: number): string {
  const lines = [`### Option ${i + 1} · ${GAP_OPTION_KIND_LABELS[o.kind] ?? o.kind}: ${o.title}`];
  if (o.kind === 'templated' && o.command) lines.push('', '```', o.command, '```');
  if (o.steps.length) lines.push('', ...o.steps.map((s, n) => `${n + 1}. ${s}`));
  return lines.join('\n');
}

/** The brief as markdown, for "Copy brief" (paste into an issue, a ticket, or an agent prompt). */
export function briefMarkdown(brief: FulfillmentBrief): string {
  return [
    `# Gap brief · ${brief.gap_key}`,
    '',
    `**What:** ${brief.what}`,
    '',
    `**Why:** ${brief.why}`,
    '',
    ...brief.options.map(optionMarkdown),
    '',
    `**Closes when:** ${brief.closing_observation.description}`,
    ...(brief.citations.length ? ['', `Citations: ${brief.citations.map((c) => `${c.kind}:${c.ref}`).join(', ')}`] : []),
    '',
    `Ref: ${brief.brief_ref}`,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Outbound webhooks (V114-2-6; admin only — every call below answers 403 under admin)
// ---------------------------------------------------------------------------

export type { OutboundWebhookDelivery, OutboundWebhookEndpoint };

export const listOutboundWebhooks = (slug: string) =>
  req<{ endpoints: OutboundWebhookEndpoint[]; limit: number }>(`${ws(slug)}/webhooks`);

/** The FULL signing secret comes back exactly ONCE; only a hash and a 4-char hint are stored. */
export const createOutboundWebhook = (slug: string, input: { url: string; label?: string }) =>
  send<{ endpoint: OutboundWebhookEndpoint; secret: string; note: string }>(`${ws(slug)}/webhooks`, 'POST', input);

export const deleteOutboundWebhook = (slug: string, endpointId: string) =>
  send<{ deleted: true }>(`${ws(slug)}/webhooks/${enc(endpointId)}`, 'DELETE');

/** The new secret, once. The old secret stops signing immediately. */
export const rotateOutboundWebhookSecret = (slug: string, endpointId: string) =>
  send<{ secret: string; secret_hint: string; note: string }>(`${ws(slug)}/webhooks/${enc(endpointId)}/rotate-secret`, 'POST', {});

export const listOutboundWebhookDeliveries = (slug: string, endpointId: string) =>
  req<{ endpoint_id: string; deliveries: OutboundWebhookDelivery[] }>(`${ws(slug)}/webhooks/${enc(endpointId)}/deliveries`);

/**
 * POST /webhooks/:id/test (panel H7) — one signed `webhook.test` ping, sent now. `blocked` is set
 * (with a plain `message`) when the SSRF guard refused the destination at send time: nothing was sent.
 */
export const sendOutboundWebhookTest = (slug: string, endpointId: string) =>
  send<{ test: { delivered: boolean; response_code: number | null; blocked: string | null; message?: string } }>(`${ws(slug)}/webhooks/${enc(endpointId)}/test`, 'POST', {});

/** POST /webhooks/:id/deliveries/:deliveryId/redeliver (panel H7) — re-send one failed / dead-lettered delivery now. */
export const redeliverOutboundWebhook = (slug: string, endpointId: string, deliveryId: string) =>
  send<{ redelivered: { delivery: OutboundWebhookDelivery; delivered: boolean; blocked: string | null; message?: string } }>(
    `${ws(slug)}/webhooks/${enc(endpointId)}/deliveries/${enc(deliveryId)}/redeliver`, 'POST', {});

/** A delivery the SSRF guard refused at send time (migration 875) reads as blocked, not as a receiver error. */
export const BLOCKED_DELIVERY_LABEL = 'Blocked — private or internal address';

/** Delivery states in people's words; a dead-lettered delivery failed and its retries are exhausted. */
export const DELIVERY_STATE_LABELS: Record<string, string> = {
  pending: 'Pending', delivered: 'Delivered', failed: 'Failed', dead_letter: 'Delivery failed — retries exhausted',
};
export const deliveryStateLabel = (s: string): string => DELIVERY_STATE_LABELS[s] ?? humanize(s);
